import { Lightning, X } from '@phosphor-icons/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import {
  DealCountdown,
  ProductLogo,
  useDealRemainingMs,
  type ProductDto,
} from '@entities/product';
import { haptics } from '@shared/lib/telegram';
import { useLogoAccent } from '@shared/lib/useLogoAccent';
import { Button } from '@shared/ui/Button';
import styles from './FlashDealHero.module.css';

interface FlashDealHeroProps {
  product: ProductDto;
  clockOffsetMs: number;
  onBuy: (product: ProductDto) => void;
}

export function FlashDealHero({ product, clockOffsetMs, onBuy }: FlashDealHeroProps) {
  const [open, setOpen] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();
  const endsAt = product.flashDeal?.endsAt ?? new Date(0).toISOString();
  const remaining = useDealRemainingMs(endsAt, clockOffsetMs);
  const accent = useLogoAccent(product.logoUrl, '#f5a623');

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'Tab') {
        const focusable = dialog.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (remaining <= 0) setOpen(false);
  }, [remaining]);

  if (!product.flashDeal || remaining <= 0) return null;

  const show = () => {
    haptics.tap('light');
    setOpen(true);
  };

  return (
    <>
      <motion.button
        type="button"
        className={styles.hero}
        style={{
          '--banner-accent': accent.color,
          '--banner-accent-ink': accent.ink,
        } as CSSProperties}
        onClick={show}
        initial={reducedMotion ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileTap={reducedMotion ? undefined : { scale: 0.985 }}
        aria-label={`Open flash deal for ${product.name}`}
      >
        <ProductLogo
          className={styles.heroProduct}
          src={product.logoUrl}
          alt=""
          width={112}
          height={112}
          loading="eager"
        />
        <span className={styles.heroCopy}>
          <span className={styles.eyebrow}>
            <Lightning size={13} weight="fill" /> Flash deal
          </span>
          <strong className={styles.heroName}>{product.name}</strong>
          <DealCountdown
            className={styles.heroCountdown}
            endsAt={product.flashDeal.endsAt}
            clockOffsetMs={clockOffsetMs}
          />
          <span className={styles.heroPrices}>
            {product.listPrice ? <s>{product.listPrice.label}</s> : null}
            <strong>{product.price.label}</strong>
          </span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open ? (
          <motion.div
            className={styles.scrim}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setOpen(false);
            }}
          >
            <motion.section
              ref={dialog}
              className={styles.sheet}
              role="dialog"
              aria-modal="true"
              aria-labelledby="flash-deal-title"
              initial={reducedMotion ? false : { y: '100%' }}
              animate={{ y: 0 }}
              exit={reducedMotion ? { opacity: 0 } : { y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              drag={reducedMotion ? false : 'y'}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.35 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 120) setOpen(false);
              }}
            >
              <div className={styles.grabber} aria-hidden />
              <button
                ref={closeButton}
                type="button"
                className={styles.close}
                aria-label="Close flash deal"
                onClick={() => setOpen(false)}
              >
                <X size={18} weight="bold" />
              </button>

              <div className={styles.sheetContent}>
                <span className={styles.sheetEyebrow}>
                  <Lightning size={14} weight="fill" /> Limited-time offer
                </span>
                <ProductLogo
                  className={styles.sheetProduct}
                  src={product.logoUrl}
                  alt={product.name}
                  width={112}
                  height={112}
                  loading="eager"
                />
                <h2 id="flash-deal-title" className={styles.sheetName}>
                  {product.name}
                </h2>
                {product.discountLabel ? (
                  <p className={styles.saving}>{product.discountLabel}</p>
                ) : null}
                <div className={styles.sheetTimer}>
                  <span>Offer ends in</span>
                  <DealCountdown
                    endsAt={product.flashDeal.endsAt}
                    clockOffsetMs={clockOffsetMs}
                  />
                </div>
                <p className={styles.sheetPrices}>
                  {product.listPrice ? <s>{product.listPrice.label}</s> : null}
                  <strong>{product.price.label}</strong>
                </p>
                <Button
                  fullWidth
                  onClick={() => {
                    setOpen(false);
                    onBuy(product);
                  }}
                >
                  Purchase for {product.price.label}
                </Button>
              </div>
            </motion.section>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
